(() => {
  const ROOT_SEL = '[data-vtc]';

  const ICON_SPINNER = `
    <svg class="vtc-spinner" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" opacity=".25"></circle>
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"
              stroke-linecap="round" stroke-dasharray="18 64"></circle>
    </svg>`;
  const ICON_CHECK = `
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;
  const ICON_X = `
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

  const qs = (el, sel) => (el ? el.querySelector(sel) : null);
  const qsa = (el, sel) => (el ? Array.from(el.querySelectorAll(sel)) : []);

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function createIframe(videoId) {
    const origin = encodeURIComponent(window.location.origin);
    const src =
      `https://www.youtube.com/embed/${encodeURIComponent(videoId)}` +
      `?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${origin}`;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');
    iframe.loading = 'lazy';
    return iframe;
  }

  function ytCommand(iframe, func, args = []) {
    try {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func, args }),
        '*'
      );
    } catch (_) {}
  }

  function setIframeMuted(iframe, muted) {
    ytCommand(iframe, muted ? 'mute' : 'unMute');
    ytCommand(iframe, 'playVideo');
  }

  function bootSection(section) {
    if (!section || section.dataset.vtcInit === '1') return;
    section.dataset.vtcInit = '1';

    const sectionId = section.dataset.sectionId;
    const slider = qs(section, `#Slider-${sectionId}`);
    if (!slider) return;

    const slides = qsa(section, '[data-vtc-slide]');
    if (!slides.length) return;

    const prevBtn = qs(section, '.slider-button--prev');
    const nextBtn = qs(section, '.slider-button--next');

    const counterCurrent = qs(section, '.slider-counter--current');
    const counterTotal = qs(section, '.slider-counter--total');
    if (counterTotal) counterTotal.textContent = String(slides.length);

    const autoAdvanceEnabled = section.dataset.autoAdvance === 'true';
    const autoplaySetting = section.dataset.autoplay === 'true';
    const autoplayEnabled = autoplaySetting && !prefersReducedMotion();

    const speedMs = Math.max(5, parseInt(section.dataset.speed || '8', 10)) * 1000;

    let activeIndex = 0;
    let timer = null;

    // engaged = user unmuted; stop auto-advance
    let engaged = false;
    let hovered = false;
    let inViewport = true;

    // Lock to prevent IO from snapping to the “wrong” slide during programmatic scroll
    let programmaticLockUntil = 0;

    // mute state per slide
    const mutedMap = new Map();
    slides.forEach((_, i) => mutedMap.set(i, true));

    function setCounter(idx) {
      if (counterCurrent) counterCurrent.textContent = String(idx + 1);
    }

    function cleanupNonActiveIframes() {
      slides.forEach((s, i) => {
        if (i === activeIndex) return;
        const iframe = qs(s, 'iframe');
        if (iframe) iframe.remove();

        const btn = qs(s, '[data-vtc-toggle-audio]');
        if (btn) {
          btn.textContent = 'Unmute';
          btn.setAttribute('aria-pressed', 'false');
          btn.classList.remove('is-active');
        }
        mutedMap.set(i, true);
      });
    }

    function ensureActiveIframe() {
      if (!autoplayEnabled && !engaged) return;

      const slide = slides[activeIndex];
      if (!slide) return;

      const id = slide.dataset.ytid;
      if (!id) return;

      const wrap = qs(slide, '[data-vtc-video]');
      if (!wrap) return;

      let iframe = qs(wrap, 'iframe');
      if (!iframe) {
        iframe = createIframe(id);
        wrap.appendChild(iframe);
      }

      const muted = mutedMap.get(activeIndex) !== false;
      setIframeMuted(iframe, muted);

      const btn = qs(slide, '[data-vtc-toggle-audio]');
      if (btn) {
        btn.textContent = muted ? 'Unmute' : 'Mute';
        btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
        btn.classList.toggle('is-active', !muted);
      }
    }

    function setActive(idx) {
      activeIndex = Math.max(0, Math.min(idx, slides.length - 1));
      setCounter(activeIndex);
      cleanupNonActiveIframes();
      ensureActiveIframe();
    }

    function stopAuto() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startAuto() {
      if (!autoAdvanceEnabled) return;
      if (slides.length <= 1) return;
      if (!inViewport) return;
      if (hovered) return;
      if (engaged) return;

      stopAuto();
      timer = window.setInterval(() => {
        if (engaged || hovered || !inViewport) return;

        const next = (activeIndex + 1) % slides.length;
        goToIndex(next, 'smooth');
      }, speedMs);
    }

    function pauseAutoTemp(ms = 1200) {
      stopAuto();
      if (!engaged) window.setTimeout(startAuto, ms);
    }

    function scrollToIndex(idx, behavior = 'smooth') {
      const target = slides[idx];
      if (!target) return;

      // Special-case idx 0: scrollLeft = 0 is the most reliable loop behavior in Spotlight/Dawn sliders
      if (idx === 0) {
        slider.scrollTo({ left: 0, behavior });
        return;
      }

      const styles = window.getComputedStyle(slider);
      const padLeft = parseFloat(styles.paddingLeft || '0') || 0;
      const left = Math.max(0, target.offsetLeft - padLeft);
      slider.scrollTo({ left, behavior });
    }

    function goToIndex(idx, behavior = 'smooth') {
      // Lock observer updates while we programmatically scroll
      programmaticLockUntil = Date.now() + 900;

      engaged = false;
      setActive(idx);
      scrollToIndex(idx, behavior);

      // Re-assert active after scroll settles (fixes “loop to start skips product 1”)
      window.setTimeout(() => {
        if (Date.now() <= programmaticLockUntil + 200) {
          setActive(idx);
        }
      }, 380);

      startAuto();
    }

    function toggleAudioForActive() {
      const slide = slides[activeIndex];
      if (!slide) return;

      const id = slide.dataset.ytid;
      if (!id) return;

      ensureActiveIframe();
      const iframe = qs(slide, 'iframe');
      if (!iframe) return;

      const currentlyMuted = mutedMap.get(activeIndex) !== false;
      const nextMuted = !currentlyMuted;

      mutedMap.set(activeIndex, nextMuted);
      setIframeMuted(iframe, nextMuted);

      const btn = qs(slide, '[data-vtc-toggle-audio]');
      if (btn) {
        btn.textContent = nextMuted ? 'Unmute' : 'Mute';
        btn.setAttribute('aria-pressed', nextMuted ? 'false' : 'true');
        btn.classList.toggle('is-active', !nextMuted);
      }

      engaged = !nextMuted; // unmuted => engaged
      if (engaged) stopAuto();
      else startAuto();
    }

    /* ===== Active slide detection (IntersectionObserver) ===== */
    const vis = new Map(slides.map((_, i) => [i, 0]));
    let slideObserver = null;

    if ('IntersectionObserver' in window) {
      slideObserver = new IntersectionObserver(
        (entries) => {
          if (Date.now() < programmaticLockUntil) return;

          entries.forEach((e) => {
            const i = Number(e.target?.dataset?.index || 0);
            vis.set(i, e.intersectionRatio || 0);
          });

          let bestI = activeIndex;
          let bestR = -1;
          vis.forEach((r, i) => {
            if (r > bestR) { bestR = r; bestI = i; }
          });

          if (bestI !== activeIndex && bestR >= 0.55) {
            engaged = false;
            setActive(bestI);
            startAuto();
          }
        },
        { root: slider, threshold: [0.35, 0.55, 0.75] }
      );

      slides.forEach((s) => slideObserver.observe(s));
    }

    /* ===== Section viewport pause ===== */
    let sectionObserver = null;
    if ('IntersectionObserver' in window) {
      sectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          inViewport = !!entry?.isIntersecting;

          if (!inViewport) {
            stopAuto();
            if (!engaged) cleanupNonActiveIframes();
          } else {
            ensureActiveIframe();
            startAuto();
          }
        },
        { threshold: [0.15] }
      );
      sectionObserver.observe(section);
    }

    /* ===== Click behavior ===== */

    // Clicking a non-active slide scrolls to it
    slides.forEach((slide, i) => {
      slide.addEventListener('click', (e) => {
        if (e.target.closest('button, a, input, select, textarea, [data-vtc-add]')) return;
        if (i !== activeIndex) {
          goToIndex(i, 'smooth');
          pauseAutoTemp(1200);
        }
      });
    });

    // Video area click toggles audio (active slide) OR navigates to slide
    slides.forEach((slide, i) => {
      const videoWrap = qs(slide, '[data-vtc-video]');
      const btn = qs(slide, '[data-vtc-toggle-audio]');

      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (i !== activeIndex) {
          goToIndex(i, 'smooth');
          pauseAutoTemp(1200);
          return;
        }
        toggleAudioForActive();
      };

      btn?.addEventListener('click', handler);
      videoWrap?.addEventListener('click', (e) => {
        if (e.target.closest('button')) return;
        handler(e);
      });
    });

    // Arrows: let theme move scroll, then force-sync active video
    prevBtn?.addEventListener('click', () => {
      engaged = false;
      programmaticLockUntil = Date.now() + 700;
      pauseAutoTemp(1200);
      window.setTimeout(() => ensureActiveIframe(), 260);
    });
    nextBtn?.addEventListener('click', () => {
      engaged = false;
      programmaticLockUntil = Date.now() + 700;
      pauseAutoTemp(1200);
      window.setTimeout(() => ensureActiveIframe(), 260);
    });

    // Hover/focus pauses auto-advance
    section.addEventListener('mouseenter', () => { hovered = true; stopAuto(); });
    section.addEventListener('mouseleave', () => { hovered = false; startAuto(); });
    section.addEventListener('focusin', () => { stopAuto(); });
    section.addEventListener('focusout', () => { startAuto(); });

    // CTA interactions pause auto-advance briefly
    qsa(section, '[data-vtc-add], .vtc__shop, .vtc__shopLink').forEach((el) => {
      el.addEventListener('click', () => pauseAutoTemp(1500));
    });

    /* ===== Add to cart (“+”) — matches your audio-preview approach ===== */
    qsa(section, '[data-vtc-add]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        btn.setAttribute('disabled', 'disabled');

        const original = btn.innerHTML;
        btn.innerHTML = ICON_SPINNER;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 1 })
          });

          if (!res.ok) throw new Error('Add to cart failed');

          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          setTimeout(() => {
            const navbar = document.querySelector('[class*="ai-navbar"]');
            if (navbar && typeof navbar.showCartDropdown === 'function') navbar.showCartDropdown();
          }, 80);

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
          const notif = document.querySelector('cart-notification');
          if (notif && typeof notif.open === 'function') notif.open();

          btn.innerHTML = ICON_CHECK;
          setTimeout(() => {
            btn.innerHTML = original;
            btn.removeAttribute('disabled');
          }, 900);
        } catch (_) {
          btn.innerHTML = ICON_X;
          setTimeout(() => {
            btn.innerHTML = original;
            btn.removeAttribute('disabled');
          }, 900);
        }
      });
    });
        /* ===== Buy Now (force direct-to-checkout) ===== */
    qsa(section, '[data-buy-now-form]').forEach((form) => {
      if (form.dataset.buyNowInit === '1') return;
      form.dataset.buyNowInit = '1';

      // Capture phase to beat theme/cart-drawer submit interceptors
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        const btn = form.querySelector('button[type="submit"], input[type="submit"]');
        const originalBtnHTML = btn ? btn.innerHTML : '';

        const returnTo =
          qs(form, 'input[name="return_to"]')?.value ||
          ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
            ? `${window.Shopify.routes.root}checkout`
            : '/checkout');

        try {
          if (btn) {
            btn.setAttribute('disabled', 'disabled');
            // optional feedback (safe)
            if (originalBtnHTML && originalBtnHTML.length < 60) btn.innerHTML = '...';
          }

          const fd = new FormData(form);
          // safety: ensure qty exists
          if (!fd.get('quantity')) fd.set('quantity', '1');

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: fd
          });

          if (!res.ok) throw new Error('Buy Now add failed');

          // hard redirect to checkout (skip cart page)
          window.location.assign(returnTo);
        } catch (err) {
          // fallback: normal submit (may go to cart depending on theme)
          try { form.submit(); }
          catch (_) { window.location.assign('/checkout'); }
        } finally {
          if (btn) {
            btn.removeAttribute('disabled');
            if (originalBtnHTML) btn.innerHTML = originalBtnHTML;
          }
        }
      }, true);
    });


    /* ===== INIT ===== */
    programmaticLockUntil = Date.now() + 900;
    setActive(0);
    scrollToIndex(0, 'auto');
    ensureActiveIframe();
    startAuto();

    section.__vtcCleanup = () => {
      stopAuto();
      slideObserver?.disconnect?.();
      sectionObserver?.disconnect?.();
    };
  }

  function bootAll(root = document) {
    qsa(root, ROOT_SEL).forEach(bootSection);
  }

  document.addEventListener('DOMContentLoaded', () => bootAll());

  document.addEventListener('shopify:section:load', (e) => {
    bootAll(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = qs(e.target, ROOT_SEL);
    if (el && typeof el.__vtcCleanup === 'function') el.__vtcCleanup();
  });
})();
